import iBonnLogo from "assets/ibonn-icon.svg";
import { formatOre } from "pages/CampaignPage/campaignKind";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Icon, IconName, styles as ui } from "stories/Admin";
import {
  DEEP_LINK_DELAY_MS,
  DonationResult,
  DonationStatusView,
  POLL_INTERVAL_MS,
  POLL_MAX_MS,
  appDeepLink,
  donationStatusUrl,
  openDeepLink,
  parseDonationStatus,
} from "./donationResult";
import styles from "./DonationResultPage.module.scss";

/*
 * Offentlige sider som Stripe og Vipps sender giveren tilbake til etter en gave fra appen
 * (`/donasjon/takk?d={id}&t={token}` og `/donasjon/avbrutt?d=…&t=…`). Uten innlogging.
 * Med d og t prøver siden å åpne appen igjen, og henter den ekte statusen fra
 * `GET /api/v1/donations/{d}/status?t={t}`. Feiler det, vises den faste teksten.
 */

const HOME_URL = "https://xn--ibnn-hra.no";

interface View {
  title: string;
  icon: IconName;
  tone: "ok" | "neutral";
  text: React.ReactNode;
}

const staticContent: Record<DonationResult, View & { docTitle: string }> = {
  thanks: {
    title: "Takk for gaven!",
    icon: "heart",
    tone: "ok",
    text: "Gaven går rett til moskeen. Du får kvittering fra Vipps eller Stripe. Er det en fast månedlig gave, kan du stoppe den der når du vil.",
    docTitle: "Takk for gaven – iBønn",
  },
  cancelled: {
    title: "Betalingen ble avbrutt",
    icon: "x",
    tone: "neutral",
    text: "Ingen penger er trukket. Gå tilbake til iBønn-appen hvis du vil prøve igjen.",
    docTitle: "Betalingen ble avbrutt – iBønn",
  },
};

/** Teksten for en kjent status, eller null (da brukes den faste teksten). */
const statusContent = (
  s: DonationStatusView,
  pollingDone: boolean,
): View | null => {
  if (s.kind === "paid") {
    const amount = s.amountOre != null ? formatOre(s.amountOre) : null;
    const what = amount
      ? `${amount}${s.monthly ? " per måned" : ""}`
      : s.monthly
        ? "Fast månedlig gave"
        : "Gaven";
    return {
      title: "Gaven er registrert",
      icon: "heart",
      tone: "ok",
      text: (
        <>
          <span className={styles.amount}>
            {what}
            {s.campaignName && (
              <>
                {" "}
                til <b>{s.campaignName}</b>
              </>
            )}
          </span>
          <span>
            Takk! Gaven går rett til moskeen, og du får kvittering fra Vipps
            eller Stripe.
            {s.monthly &&
              " Den faste månedlige gaven kan du stoppe der når du vil."}
          </span>
        </>
      ),
    };
  }
  if (s.kind === "pending") {
    return {
      title: "Venter på bekreftelse\u00a0…",
      icon: "clock",
      tone: "neutral",
      text: pollingDone
        ? "Det tar litt lengre tid enn vanlig. Du får kvittering fra Vipps eller Stripe når betalingen er gjennomført."
        : "Vi venter på svar fra Vipps eller Stripe. Det tar vanligvis bare noen sekunder.",
    };
  }
  if (s.kind === "cancelled") {
    return {
      title: "Betalingen ble avbrutt",
      icon: "x",
      tone: "neutral",
      text: s.monthly
        ? "Den faste gaven ble ikke startet, og ingen penger er trukket. Gå tilbake til iBønn-appen hvis du vil prøve igjen."
        : "Ingen penger er trukket. Gå tilbake til iBønn-appen hvis du vil prøve igjen.",
    };
  }
  if (s.kind === "failed") {
    return {
      title: "Betalingen gikk ikke gjennom",
      icon: "x",
      tone: "neutral",
      text: "Ingen penger er trukket. Gå tilbake til iBønn-appen hvis du vil prøve igjen.",
    };
  }
  return null;
};

/** Mens den første statusen hentes: ikke takk før vi vet at gaven gikk gjennom. */
const checkingContent: View = {
  title: "Sjekker betalingen\u00a0…",
  icon: "clock",
  tone: "neutral",
  text: "Vi henter svaret fra Vipps eller Stripe.",
};

/** Status kunne ikke hentes eller var ukjent, og Vipps/Stripe sendte oss til takkesiden. */
const unknownContent: View = {
  title: "Tilbake fra betalingen",
  icon: "heart",
  tone: "neutral",
  text: "Vi fikk ikke sjekket om betalingen gikk gjennom. Gikk den gjennom, får du kvittering fra Vipps eller Stripe. Du ser også statusen i iBønn-appen.",
};

/** Henter status; null ved alle feil (CORS, App Check, 404, nettverk …). */
const fetchStatus = async (d: string, t: string) => {
  try {
    const res = await fetch(donationStatusUrl(d, t), {
      headers: { accept: "application/json" },
    });
    if (!res.ok) return null;
    return parseDonationStatus(await res.json());
  } catch {
    return null;
  }
};

interface Props {
  result: DonationResult;
}

const DonationResultPage: React.FC<Props> = ({ result }) => {
  const [params] = useSearchParams();
  const d = params.get("d");
  const t = params.get("t");
  const deepLink = appDeepLink(result, d, t);
  const [status, setStatus] = useState<DonationStatusView | null>(null);
  const [pollingDone, setPollingDone] = useState(false);
  const fallback = staticContent[result];

  useEffect(() => {
    document.title = fallback.docTitle;
  }, [fallback.docTitle]);

  // Prøv én gang å åpne appen. Uten appen skjer ingenting, og siden virker som før.
  useEffect(() => {
    if (!d || !t) return;
    const timer = window.setTimeout(() => {
      openDeepLink(appDeepLink(result, d, t));
    }, DEEP_LINK_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [result, d, t]);

  // Ekte status; venter (PENDING) sjekkes hvert 3. sekund i opptil 30 sekunder.
  useEffect(() => {
    if (!d || !t) return;
    let cancelled = false;
    let timer: number | undefined;
    const started = Date.now();
    const check = async () => {
      const s = await fetchStatus(d, t);
      if (cancelled) return;
      if (!s) {
        // Feil: behold det vi har (eller den faste teksten), ingen feilmelding.
        setPollingDone(true);
        return;
      }
      setStatus(s);
      if (s.kind === "pending" && Date.now() - started < POLL_MAX_MS) {
        timer = window.setTimeout(check, POLL_INTERVAL_MS);
      } else {
        setPollingDone(true);
      }
    };
    check();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [d, t]);

  const hasToken = Boolean(d && t);
  const known = status && statusContent(status, pollingDone);
  // Vipps sender giveren hit (takk) også når betalingen ble avbrutt, så med d og t
  // sier vi aldri takk før serveren har bekreftet gaven.
  const c: View =
    known ||
    (!hasToken || result === "cancelled"
      ? fallback
      : !status && !pollingDone
        ? checkingContent
        : unknownContent);

  return (
    <main className={`${ui.root} ${styles.page}`}>
      <div className={styles.card} aria-live="polite">
        <img className={styles.logo} src={iBonnLogo} alt="iBønn" />
        <span
          className={`${styles.badge} ${
            c.tone === "ok" ? styles.ok : styles.cancelled
          } ${
            (status?.kind === "pending" || (hasToken && !status)) &&
            !pollingDone
              ? styles.pulse
              : ""
          }`}
          aria-hidden="true"
        >
          <Icon name={c.icon} size={26} strokeWidth={2.2} />
        </span>
        <h1 className={styles.title}>{c.title}</h1>
        <p className={styles.text}>{c.text}</p>
        <a className={styles.appButton} href={deepLink}>
          Tilbake til iBønn
        </a>
        <p className={styles.hint}>
          Åpnes ikke appen? Da kan du lukke denne siden og gå tilbake til iBønn
          selv.
        </p>
        <a className={styles.home} href={HOME_URL}>
          Til ibønn.no
        </a>
      </div>
    </main>
  );
};

export default DonationResultPage;
